import { fetchCalendarEvents } from '@monorepo/old-web/js/actions/calendar';
import apiCaller from '@monorepo/old-web/js/apiCaller';
import { withParamsAndNavigate } from '@monorepo/old-web/js/components/routing/withReactRouter';
import withProvider from '@monorepo/old-web/js/components/withProvider';
import APP from '@monorepo/old-web/js/config';
import { getLinkToAdminFormTemplateEditor } from '@monorepo/shared/externalLinks/adminPanel';
import { withPermissions } from '@monorepo/shared/hooks/permissions/withPermissions';
import { getCurrentWindowUrl } from '@monorepo/shared/utils/navigation/getLocationUrl';
import { navigateWithReferrer } from '@monorepo/shared/utils/navigation/navigationReferrer';
import AddIcon from '@svg/add.svg';
import CogIcon from '@svg/cog.svg';
import DocumentIcon from '@svg/document.svg';
import DownloadIcon from '@svg/download.svg';
import EditIcon from '@svg/edit.svg';
import NotesIcon from '@svg/notes.svg';
import UserIcon from '@svg/user.svg';
import {
  CalendarEventStatus,
  getFrequencyDisplayName,
  isDefaultAttachment,
} from 'mapistry-shared';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button } from '../../../elements';
import { FormCalendar, MenuOptionsType } from '../../../propTypes';
import { InspectionAssignModalContainer } from '../../../task/InspectionAssignModal';
import { CalendarCardWithProvider as CalendarCard } from '../CalendarCard';
import NeverEventCalendar from '../CalendarCard/NeverEventCalendar';
import { CannotEditFormTemplateModal } from '../CannotEditFormTemplateModal';
import { InspectionFrequencyModal } from '../InspectionFrequencyModal';
import DownloadInspectionsModal from './DownloadInspectionsModal';
import { InspectionEventChoiceMenu } from './InspectionEventChoiceMenu';

class BaseInspectionCalendarCard extends Component {
  constructor(props) {
    super(props);
    this.handleEventClick = this.handleEventClick.bind(this);
    this.handleIsDownloadModalOpen = this.handleIsDownloadModalOpen.bind(this);
    this.handleDownloadModalClose = this.handleDownloadModalClose.bind(this);
    this.handleOpenStencil = this.handleOpenStencil.bind(this);
    this.handleFrequencyModalClose = this.handleFrequencyModalClose.bind(this);
    this.handleFrequencyModalOpen = this.handleFrequencyModalOpen.bind(this);
    this.handleOpenFormTemplate = this.handleOpenFormTemplate.bind(this);
    this.handleOpenAdminFormTemplateEditor =
      this.handleOpenAdminFormTemplateEditor.bind(this);
    this.handleOpenInspectionAssign =
      this.handleOpenInspectionAssign.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleFrequencyMetadata = this.handleFrequencyMetadata.bind(this);

    this.state = {
      isInspectionAssignModalOpen: false,
      isDownloadModalOpen: false,
      eventChoices: [],
      eventChoiceTarget: null,
      isCannotEditFormTemplateModalOpen: false,
      frequencyMetadata: {
        frequency: null,
        customFrequency: null,
        canAssign: true, // default true, updates on frequencies load
      },

      // for frequency modal
      currentDate: new Date(),
      frequencyModalOpen: false,
    };
  }

  handleIsDownloadModalOpen() {
    this.setState({ isDownloadModalOpen: true });
  }

  handleDownloadModalClose() {
    this.setState({ isDownloadModalOpen: false });
  }

  handleFrequencyModalOpen() {
    this.setState({ frequencyModalOpen: true });
  }

  handleFrequencyModalClose() {
    this.setState({ frequencyModalOpen: false });
  }

  handleOpenInspectionAssign() {
    this.setState({ isInspectionAssignModalOpen: true });
  }

  handleDateChange(newDate) {
    this.setState({ currentDate: newDate });
    const { onDateChange } = this.props;
    if (onDateChange) {
      onDateChange(newDate);
    }
  }

  handleFrequencyMetadata(frequencyMetadata) {
    this.setState({ frequencyMetadata });
  }

  handleOpenStencil() {
    const { projectId, inspectionStencils, calendar } = this.props;
    if (inspectionStencils.length) {
      this.goToFormStencilEditor(
        projectId,
        inspectionStencils[0].formTemplateSlug,
        inspectionStencils[0].formSubmissionSlug,
        calendar.name,
      );
    }
  }

  handleOpenFormTemplate() {
    const { navigate, calendar, projectId, organizationId } = this.props;
    if (calendar.canEditTemplate) {
      navigate(
        `/vortex/organizations/${organizationId}/data-setup/forms/${calendar.formTemplateSlug}/edit`,
        { state: { referrerUrl: getCurrentWindowUrl(), projectId } },
      );
    } else {
      this.setState({ isCannotEditFormTemplateModalOpen: true });
    }
  }

  handleOpenAdminFormTemplateEditor() {
    const { calendar } = this.props;
    const adminEditorLink = getLinkToAdminFormTemplateEditor({
      formTemplateSlug: calendar.formTemplateSlug,
    });
    window.open(adminEditorLink, '_blank');
  }

  handleEventClick(calendarEvents, clickEvent) {
    const { calendar, projectId } = this.props;
    if (
      calendarEvents.length === 1 ||
      calendarEvents.every((e) => e.attachments.every(isDefaultAttachment))
    ) {
      // should only have 1 attachment, if there are 2, it is safe to use the first one for now
      const calendarEvent = calendarEvents[0];
      const attachment = calendarEvent.attachments[0];
      const eventDate = calendarEvent.dueDate || calendarEvent.startDate;
      const submissionUrl = apiCaller.getFormSubmissionUrl(
        projectId,
        attachment.formTemplateSlug,
        attachment.formSubmissionSlug,
        calendar.name,
        eventDate,
        attachment.eventNumber,
      );

      navigateWithReferrer(submissionUrl);
    } else {
      this.setState({
        eventChoices: calendarEvents,
        eventChoiceTarget: clickEvent.currentTarget,
      });
    }
  }

  goToFormStencilEditor(projectId, templateSlug, submissionSlug, calendarName) {
    const url = `/projects/${projectId}/form-submissions/${templateSlug}/${submissionSlug}/${calendarName}/stencil`;
    navigateWithReferrer(url);
  }

  inspectionAssignTask() {
    const { calendar } = this.props;

    return {
      calendarId: calendar.id,
      calendarName: calendar.name,
      title: calendar.summaryTitle,
    };
  }

  helpTextForEvent(event, tooMuchData) {
    if (tooMuchData) {
      return 'More Details';
    }
    switch (event.status) {
      case CalendarEventStatus.COMPLETE:
        return 'Completed inspection';
      case CalendarEventStatus.EXCEPTION:
        return 'Exception recorded';
      case CalendarEventStatus.OVERDUE:
        return 'Missed inspection';
      case CalendarEventStatus.UPCOMING:
        return 'Inspection due soon';
      case CalendarEventStatus.NOT_NEEDED:
        return 'Not Required';
      case CalendarEventStatus.EXTRA:
        return 'Create a new inspection';
      default:
        return 'Future inspection';
    }
  }

  stencilOptionLabel() {
    const { inspectionStencils } = this.props;
    return inspectionStencils.length && inspectionStencils[0].id
      ? { label: 'Edit Site Template', icon: <EditIcon className="m-icon" /> }
      : { label: 'Create Site Template', icon: <AddIcon className="m-icon" /> };
  }

  assignDisabledExplanation() {
    const { frequencyMetadata } = this.state;
    if (!frequencyMetadata || frequencyMetadata.canAssign) {
      return undefined;
    }

    return `Disabled for ${getFrequencyDisplayName(
      frequencyMetadata.frequency,
      frequencyMetadata.customFrequency,
    )} inspections`;
  }

  menuOptions() {
    const {
      calendar,
      hasProjectSettingsPermission,
      isSuperAdmin,
      menuOptions,
    } = this.props;

    const { isDownloadable } = calendar || {};

    const options = [];
    const settingsSubmenu = { label: 'Settings', items: [] };
    const formatSubmenu = { label: 'Format', items: [...menuOptions] };

    if (isDownloadable) {
      options.push({
        label: 'Download Inspections',
        icon: <DownloadIcon className="m-icon" />,
        onClick: this.handleIsDownloadModalOpen,
      });
    }

    // make settings submenu
    if (hasProjectSettingsPermission) {
      settingsSubmenu.items.push({
        label: 'Frequency Settings',
        icon: <CogIcon className="m-icon" />,
        onClick: this.handleFrequencyModalOpen,
      });
    }

    const assignDisabledExplanation = this.assignDisabledExplanation();

    settingsSubmenu.items.push({
      label: 'Assign',
      icon: <UserIcon className="m-icon" />,
      onClick: this.handleOpenInspectionAssign,
      disabledWithExplanation: assignDisabledExplanation,
    });

    // make format submenu
    if (hasProjectSettingsPermission) {
      formatSubmenu.items.push(
        {
          ...this.stencilOptionLabel(),
          onClick: this.handleOpenStencil, // site template
        },
        {
          label: 'Configure Form',
          icon: <DocumentIcon className="m-icon" />,
          onClick: this.handleOpenFormTemplate,
        },
      );
    }
    if (isSuperAdmin) {
      formatSubmenu.items.push({
        label: 'Admin Form Editor',
        icon: <NotesIcon className="m-icon" />,
        onClick: this.handleOpenAdminFormTemplateEditor,
      });
    }

    if (formatSubmenu.items.length > 0) {
      return [...options, settingsSubmenu, formatSubmenu];
    }

    return [...options, settingsSubmenu];
  }

  defaultView() {
    const { calendar } = this.props;
    return (
      <NeverEventCalendar
        calendarTitle={calendar.summaryTitle}
        additionalInfo={
          <>
            <span className="inspection-calendar-card__never__event">
              To start recording
              <strong>{` ${calendar.summaryTitle} `}</strong>
              for this time period, please click the button below to set a
              frequency.
            </span>
            <Button onClick={this.handleFrequencyModalOpen}>
              Set inspection frequency
            </Button>
          </>
        }
      />
    );
  }

  render() {
    const {
      calendar,
      defaultView,
      enableFrequencySetupPrompt,
      fetchEvents,
      tooltipData,
      tooltipTitle,
      hasProjectSettingsPermission,
      projectId,
    } = this.props;
    const {
      isInspectionAssignModalOpen,
      isDownloadModalOpen,
      frequencyModalOpen,
      eventChoices,
      eventChoiceTarget,
      isCannotEditFormTemplateModalOpen,
      currentDate,
    } = this.state;

    return (
      <>
        <CalendarCard
          calendarName={calendar.name}
          fetchEvents={fetchEvents}
          helpTextForEvent={this.helpTextForEvent}
          tooltipData={tooltipData}
          tooltipTitle={tooltipTitle}
          menuOptions={this.menuOptions()}
          defaultView={
            defaultView ||
            (enableFrequencySetupPrompt ? (
              this.defaultView()
            ) : (
              <NeverEventCalendar calendarTitle={calendar.summaryTitle} />
            ))
          }
          onDateChange={this.handleDateChange}
          onEventClick={this.handleEventClick}
          setFrequencyMetadata={this.handleFrequencyMetadata}
          title={calendar.summaryTitle}
        />
        <InspectionEventChoiceMenu
          eventChoices={eventChoices}
          menuAnchor={eventChoiceTarget}
          closeEventChoiceMenu={() =>
            this.setState({ eventChoiceTarget: null })
          }
          onEventClick={(event) => this.handleEventClick([event])}
        />

        {hasProjectSettingsPermission && frequencyModalOpen && (
          <InspectionFrequencyModal
            calendarName={calendar.name}
            onClose={this.handleFrequencyModalClose}
            open={frequencyModalOpen}
            reloadEvents={() =>
              fetchEvents(projectId, calendar.name, currentDate)
            }
            title={`${calendar.summaryTitle} Frequency Settings`}
          />
        )}
        {isInspectionAssignModalOpen && (
          <InspectionAssignModalContainer
            isOpen={isInspectionAssignModalOpen}
            onClose={() => {
              this.setState({ isInspectionAssignModalOpen: false });
              // Assignments live on the events, so reload them to keep event
              // tooltips from showing the previous assignee.
              fetchEvents(projectId, calendar.name, currentDate);
            }}
            task={this.inspectionAssignTask()}
          />
        )}
        {isDownloadModalOpen && (
          <DownloadInspectionsModal
            calendarId={calendar.id}
            onClose={this.handleDownloadModalClose}
            open={isDownloadModalOpen}
            title={`Download ${calendar.summaryTitle}s`}
          />
        )}
        {isCannotEditFormTemplateModalOpen && (
          <CannotEditFormTemplateModal
            open={isCannotEditFormTemplateModalOpen}
            onDone={() =>
              this.setState({ isCannotEditFormTemplateModalOpen: false })
            }
          />
        )}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchEvents: (projectId, calendarName, date) =>
    dispatch(
      fetchCalendarEvents({
        calendarName,
        date,
        projectId,
      }),
    ),
});

const mapStateToProps = () => ({
  projectId: APP.projectId,
});

const FormTemplateDetails = PropTypes.shape({
  canEdit: PropTypes.bool,
  formTemplateSlug: PropTypes.string,
});

const FormStencil = PropTypes.shape({
  id: PropTypes.number,
  formSubmissionSlug: PropTypes.string,
  formTemplateSlug: PropTypes.string,
});

BaseInspectionCalendarCard.propTypes = {
  calendar: FormCalendar.isRequired,
  defaultView: PropTypes.node,
  enableFrequencySetupPrompt: PropTypes.bool,
  fetchEvents: PropTypes.func.isRequired,
  formTemplateDetails: FormTemplateDetails,
  hasProjectSettingsPermission: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
  menuOptions: MenuOptionsType,
  navigate: PropTypes.func.isRequired,
  onDateChange: PropTypes.func,
  organizationId: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  tooltipData: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  tooltipTitle: PropTypes.func.isRequired,
  inspectionStencils: PropTypes.arrayOf(FormStencil),
};

BaseInspectionCalendarCard.defaultProps = {
  defaultView: null,
  enableFrequencySetupPrompt: false,
  menuOptions: [],
  onDateChange: null,
  organizationId: null,
  tooltipData: null,
  formTemplateDetails: { canEdit: false, formTemplateSlug: null },
  hasProjectSettingsPermission: false,
  isSuperAdmin: false,
  inspectionStencils: [],
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-default-export
export default withProvider(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withParamsAndNavigate(withPermissions(BaseInspectionCalendarCard))),
);
